import { create } from 'zustand';
import { midi, type MidiStatus } from '../midi/MidiBus';
import type { MidiPortInfo } from '../midi/types';

/**
 * `loading` — the loader is up and everything is being warmed: files fetched,
 * models parsed, shaders compiled.
 * `intro` — the logo screen, waiting for the first click.
 * `dissolving` — the logo is fading out and the instrument is taking over.
 * `playing` — the instrument is live.
 */
export type Phase = 'loading' | 'intro' | 'dissolving' | 'playing';

interface AppStore {
  phase: Phase;
  keyboardLoaded: boolean;
  midiStatus: MidiStatus;
  midiInputs: MidiPortInfo[];
  /** The controller being listened to, or `null` for every one attached. */
  midiInputId: string | null;
  menuOpen: boolean;
  selectedPanel: string | null;
  /** The key guide: computer-keyboard labels laid over the controls. */
  helpOpen: boolean;
  /**
   * Where the title card burns away to, in screen UV. The middle of the screen
   * until a click says otherwise.
   */
  burnOrigin: { x: number; y: number };

  ready: () => void;
  start: (origin?: { x: number; y: number }) => void;
  finishIntro: () => void;
  setKeyboardLoaded: (loaded: boolean) => void;
  setMidi: (status: MidiStatus, inputs: MidiPortInfo[]) => void;
  selectMidiInput: (id: string | null) => void;
  selectPanel: (panel: string | null) => void;
  closeMenu: () => void;
  toggleHelp: () => void;
}

export const useAppStore = create<AppStore>((set, get) => ({
  phase: 'loading',
  keyboardLoaded: false,
  midiStatus: 'idle',
  midiInputs: [],
  midiInputId: null,
  menuOpen: false,
  selectedPanel: null,
  helpOpen: false,
  burnOrigin: { x: 0.5, y: 0.5 },

  ready: () => {
    if (get().phase === 'loading') set({ phase: 'intro' });
  },
  start: (origin) => {
    if (get().phase !== 'intro') return;
    set(origin ? { phase: 'dissolving', burnOrigin: origin } : { phase: 'dissolving' });
  },
  finishIntro: () => set({ phase: 'playing' }),
  setKeyboardLoaded: (keyboardLoaded) => set({ keyboardLoaded }),
  setMidi: (midiStatus, midiInputs) => set({ midiStatus, midiInputs }),
  selectMidiInput: (midiInputId) => {
    midi.useInput(midiInputId);
    set({ midiInputId });
  },
  selectPanel: (panel) =>
    set(panel === get().selectedPanel
      ? { menuOpen: false, selectedPanel: null }
      : { menuOpen: true, selectedPanel: panel }),
  closeMenu: () => set({ menuOpen: false, selectedPanel: null }),
  toggleHelp: () => set({ helpOpen: !get().helpOpen }),
}));
